import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Phone, SpinnerGap, WarningCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';

import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import {
  isMsg91WidgetProvider,
  retryMsg91WidgetOtp,
  sendMsg91WidgetOtp,
  verifyMsg91WidgetOtp,
} from '../../services/msg91OtpWidget';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { getDashboardPath } from '../../lib/authRouting';

const maskPhone = (phone = '') => {
  const value = String(phone);
  if (value.length <= 4) return value;
  return `${value.slice(0, Math.min(3, value.length - 4))}${'•'.repeat(Math.max(2, value.length - 7))}${value.slice(-4)}`;
};

export default function InvitePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const inviteToken = useMemo(() => (searchParams.get('t') || searchParams.get('token') || '').trim(), [searchParams]);
  const { user, loading: authLoading, claimInvite, completeInviteOtp } = useAuth();

  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState('');
  const [loading, setLoading] = useState(Boolean(inviteToken));
  const [busy, setBusy] = useState(false);
  const [configLoaded, setConfigLoaded] = useState(false);
  const [otpConfig, setOtpConfig] = useState({ otp_enabled: false, otp_length: 6 });
  const otpEnabled = Boolean(otpConfig.otp_enabled);
  const otpLength = Number(otpConfig.otp_length) || 6;
  const [otpSent, setOtpSent] = useState(false);
  const [devCode, setDevCode] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpRequestId, setOtpRequestId] = useState('');
  const autoSentTokenRef = useRef('');

  const loginUrl = useMemo(() => {
    const params = new URLSearchParams();
    if (inviteToken) params.set('invite_token', inviteToken);
    if (preview?.guest_phone) params.set('phone', preview.guest_phone);
    if (preview?.event_id) params.set('event_id', preview.event_id);
    const query = params.toString();
    return query ? `/login?${query}` : '/login';
  }, [inviteToken, preview?.event_id, preview?.guest_phone]);

  useEffect(() => {
    let active = true;
    api.get('/auth/config')
      .then((res) => {
        if (active) setOtpConfig(res.data || { otp_enabled: false, otp_length: 6 });
      })
      .catch(() => {
        if (active) setOtpConfig({ otp_enabled: false, otp_length: 6 });
      })
      .finally(() => {
        if (active) setConfigLoaded(true);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    if (!inviteToken) {
      setPreviewError('Invite token is missing.');
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    api.get(`/invites/${encodeURIComponent(inviteToken)}`)
      .then((res) => {
        if (active) setPreview(res.data);
      })
      .catch((err) => {
        if (active) setPreviewError(err.response?.data?.detail || 'Invite is unavailable.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [inviteToken]);

  const sendInviteOtp = useCallback(async ({ automatic = false } = {}) => {
    setBusy(true);
    try {
      const response = await api.post('/auth/invite-otp/send', { invite_token: inviteToken });
      let requestId = '';
      if (response.data?.client_send_required && isMsg91WidgetProvider(otpConfig)) {
        const widgetResult = await sendMsg91WidgetOtp(
          otpConfig,
          response.data.phone || preview?.guest_phone,
        );
        requestId = widgetResult.requestId;
      }
      setOtpSent(true);
      setOtpRequestId(requestId);
      setDevCode(response.data?.dev_code || '');
      toast.success(response.data?.dev_code ? `Dev OTP: ${response.data.dev_code}` : 'OTP sent');
    } catch (error) {
      if (!automatic) toast.error(error.response?.data?.detail || 'Failed to send OTP');
      else setPreviewError(error.response?.data?.detail || 'Could not send the invite OTP.');
    } finally {
      setBusy(false);
    }
  }, [inviteToken, otpConfig, preview?.guest_phone]);

  useEffect(() => {
    if (user || !preview || !configLoaded || !otpEnabled || autoSentTokenRef.current === inviteToken) return;
    autoSentTokenRef.current = inviteToken;
    sendInviteOtp({ automatic: true });
  }, [configLoaded, inviteToken, otpEnabled, preview, sendInviteOtp, user]);

  useEffect(() => {
    if (user || !preview || !configLoaded || otpEnabled) return;
    navigate(loginUrl, { replace: true });
  }, [configLoaded, loginUrl, navigate, otpEnabled, preview, user]);

  const finishClaim = async () => {
    const result = await claimInvite(inviteToken);
    toast.success('Invite accepted');
    navigate(getDashboardPath(result.effective_role || result.role));
  };

  const handleClaimForLoggedInUser = async () => {
    setBusy(true);
    try {
      await finishClaim();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not accept invite');
    } finally {
      setBusy(false);
    }
  };

  const handleVerifyInviteOtp = async (event) => {
    event.preventDefault();
    if (!otpCode.trim()) return;
    if (otpCode.trim().length !== otpLength) {
      toast.error(`Please enter the complete ${otpLength}-digit OTP`);
      return;
    }
    setBusy(true);
    try {
      let otpAccessToken = null;
      if (isMsg91WidgetProvider(otpConfig)) {
        const widgetResult = await verifyMsg91WidgetOtp(otpConfig, otpCode.trim(), otpRequestId);
        otpAccessToken = widgetResult.accessToken;
      }
      const authUser = await completeInviteOtp(
        inviteToken,
        otpCode.trim(),
        preview?.event_id,
        { otpAccessToken },
      );
      toast.success('Phone verified and invite accepted');
      navigate(getDashboardPath(authUser.effective_role || authUser.role));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'OTP verification failed');
    } finally {
      setBusy(false);
    }
  };

  const resendInviteOtp = async () => {
    if (!isMsg91WidgetProvider(otpConfig)) {
      await sendInviteOtp();
      return;
    }
    setBusy(true);
    try {
      await retryMsg91WidgetOtp(otpConfig, otpRequestId);
      toast.success('OTP resent');
    } catch (error) {
      toast.error(error.message || 'Failed to resend OTP');
    } finally {
      setBusy(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-wedding-cream">
        <div className="h-14 w-14 animate-spin rounded-full border-4 border-wedding-sage border-t-transparent" />
      </div>
    );
  }

  if (previewError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-wedding-cream p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><WarningCircle className="h-5 w-5" /> Invite unavailable</CardTitle>
            <CardDescription>{previewError}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild className="w-full"><Link to="/login">Go to login</Link></Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-wedding-cream to-wedding-sage-light p-4">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <img src="/assets/logo.png" alt="GalaGo logo" className="mx-auto mb-3 h-14 w-14 object-contain" />
          <h1 className="font-serif text-3xl font-bold text-wedding-charcoal">GalaGo</h1>
        </div>
        <Card className="border-0 shadow-xl">
          <CardHeader>
            <CardTitle className="font-serif text-2xl">Join {preview?.event_name || 'event'}</CardTitle>
            <CardDescription>
              {preview?.guest_name ? `${preview.guest_name}, ` : ''}
              verify the invited phone number to continue.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {user ? (
              <div className="space-y-3">
                <div className="rounded-md border bg-muted/40 p-3 text-sm">
                  Signed in as <strong>{user.name}</strong> ({user.phone}).
                </div>
                <Button type="button" className="w-full bg-wedding-sage hover:bg-wedding-sage/90" onClick={handleClaimForLoggedInUser} disabled={busy}>
                  <CheckCircle className="mr-2 h-4 w-4" />
                  {busy ? 'Accepting...' : 'Accept Invite'}
                </Button>
              </div>
            ) : !otpEnabled && configLoaded ? (
              <div className="space-y-3">
                <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                  OTP sign-in is temporarily unavailable. Please sign in and reopen this invite.
                </p>
                <Button asChild variant="outline" className="w-full"><Link to={loginUrl}>Go to login</Link></Button>
              </div>
            ) : (
              <form onSubmit={handleVerifyInviteOtp} className="space-y-4">
                <div className="rounded-lg border bg-muted/30 p-3 text-sm">
                  <div className="flex items-center gap-2 font-medium">
                    <Phone className="h-4 w-4 text-wedding-sage" />
                    OTP sent to {maskPhone(preview?.guest_phone)}
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Existing accounts will be signed in. New guests will be securely created and linked to this invite.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="invite-otp">OTP Code</Label>
                  <Input
                    id="invite-otp"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    autoFocus
                    value={otpCode}
                    onChange={(event) => setOtpCode(event.target.value.replace(/\D/g, '').slice(0, otpLength))}
                    placeholder={`Enter ${otpLength}-digit OTP`}
                    maxLength={otpLength}
                    disabled={!otpSent || busy}
                  />
                  {devCode && <p className="text-xs text-orange-600">Dev OTP: <code>{devCode}</code></p>}
                </div>
                <Button type="submit" className="w-full bg-wedding-sage hover:bg-wedding-sage/90" disabled={busy || !otpSent || !otpCode.trim()}>
                  {busy ? <SpinnerGap className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle className="mr-2 h-4 w-4" />}
                  Verify & Open Invite
                </Button>
                <Button type="button" variant="ghost" className="w-full" onClick={resendInviteOtp} disabled={busy}>
                  Resend OTP
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
